import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AbsenceCalendar } from '@/components/AbsenceApplication/AbsenceCalendar';
import { Button } from '@/components/Button';
import { useStudyYearPeriod } from '@/pages/Overview/hooks/useStudyYearPeriod';
import { createCustomPeriod, hasSameDates } from '@/pages/Overview/period';
import { ButtonVariant, Size } from '@/types/components';
import type { StudyYearPeriod } from '@/types/overview';
import { formatDate } from '@/utils/date';
import { tv } from '@/utils/tv';

enum DateField {
  From = 'from',
  To = 'to',
}

interface Props {
  periods: StudyYearPeriod[];
  value: StudyYearPeriod;
  isAlignedRight: boolean;
  isCalendarOpen: boolean;
  onCalendarOpenChange: (isOpen: boolean) => void;
  onApply: (period: StudyYearPeriod) => void;
}

const chip = tv({
  base: 'flex h-8 cursor-pointer items-center rounded-3xl px-3 text-caption-12 text-primary transition-colors',
  variants: {
    isSelected: {
      true: 'bg-info-surface',
      false: 'bg-popover shadow-card hover:bg-info-surface',
    },
  },
});

const dateField = tv({
  base: 'flex h-11 w-full cursor-pointer items-center rounded-xl px-4 text-left text-body-16 text-foreground transition-colors',
  variants: {
    isActive: {
      true: 'bg-secondary-hover',
      false: 'bg-on-primary-surface hover:bg-secondary-hover',
    },
  },
});

export const PeriodRangePopover: React.FC<Props> = ({
  periods,
  value,
  isAlignedRight,
  isCalendarOpen,
  onCalendarOpenChange,
  onApply,
}) => {
  const { t } = useTranslation();
  const studyYearPeriod = useStudyYearPeriod();
  const [draft, setDraft] = useState<StudyYearPeriod>(value);
  const [editedField, setEditedField] = useState(DateField.From);

  const matchingPeriod = periods.find((period) => hasSameDates(period, draft));
  const isEditingFrom = editedField === DateField.From;

  const handleFieldClick = (field: DateField) => {
    const isSameFieldOpen = isCalendarOpen && editedField === field;

    setEditedField(field);
    onCalendarOpenChange(!isSameFieldOpen);
  };

  const handleDateChange = (date: Date) => {
    onCalendarOpenChange(false);

    if (isEditingFrom) {
      setDraft(createCustomPeriod(date, draft.to));
      return;
    }

    setDraft(createCustomPeriod(draft.from, date));
  };

  const dateFields = [
    {
      field: DateField.From,
      label: t('absenceApplication.from'),
      date: draft.from,
    },
    {
      field: DateField.To,
      label: t('absenceApplication.to'),
      date: draft.to,
    },
  ];

  const handleSave = () => onApply(matchingPeriod ?? draft);

  const handlePointerDown = (event: React.MouseEvent) => {
    const target = event.target as Element;
    const isInsideCalendarLayer = !!target.closest(
      '[data-calendar], [data-date-field]',
    );

    if (isCalendarOpen && !isInsideCalendarLayer) {
      onCalendarOpenChange(false);
    }
  };

  return (
    <div
      onMouseDown={handlePointerDown}
      className={`absolute top-full z-50 mt-2 flex w-[400px] flex-col rounded-[14px] bg-card p-4 shadow-card ${isAlignedRight ? 'right-0' : 'left-0'}`}
    >
      <h3 className="text-callout-16 text-foreground">
        {t('overview.ratings.period')}
      </h3>

      <div className="mt-4 flex flex-wrap gap-2">
        {periods.map((period) => (
          <button
            key={period.name}
            type="button"
            onClick={() => setDraft(period)}
            className={chip({ isSelected: period === matchingPeriod })}
          >
            {period.name}
          </button>
        ))}
      </div>

      <div className="relative mt-4 flex items-end gap-2">
        {dateFields.map(({ field, label, date }) => (
          <React.Fragment key={field}>
            {field === DateField.To && (
              <span className="flex h-11 items-center text-body-14 text-subtle-foreground">
                –
              </span>
            )}
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="text-body-14 text-foreground">{label}</span>
              <button
                data-date-field
                type="button"
                onClick={() => handleFieldClick(field)}
                className={dateField({
                  isActive: isCalendarOpen && editedField === field,
                })}
              >
                {formatDate(date.toISOString())}
              </button>
            </div>
          </React.Fragment>
        ))}
        {isCalendarOpen && (
          <div
            data-calendar
            className="absolute top-full left-1/2 z-10 mt-2 w-[375px] -translate-x-1/2 rounded-xl bg-card px-4 py-3.5 shadow-card"
          >
            <AbsenceCalendar
              hasFixedWeekCount
              key={editedField}
              filledDate={isEditingFrom ? draft.from : draft.to}
              minDate={isEditingFrom ? studyYearPeriod.from : draft.from}
              maxDate={isEditingFrom ? draft.to : studyYearPeriod.to}
              onChange={handleDateChange}
            />
          </div>
        )}
      </div>

      <Button
        variant={ButtonVariant.Primary}
        size={Size.Medium}
        className="mt-4 self-end px-3 text-subhead-14"
        onClick={handleSave}
      >
        {t('overview.ratings.save')}
      </Button>
    </div>
  );
};
